import { motion, useReducedMotion } from "framer-motion";
import { Check, FileVideo, Instagram, MousePointer2, Music2, Play, Scissors, Send, Sparkles, UploadCloud, UserRound, Youtube } from "lucide-react";

// Keep opacity, cursor position and transforms on one clock; mixed WAAPI/JS playback can drift.
const syncTimeline = () => {};
const loop = (seconds) => ({ duration: 12, repeat: Infinity, ease: "easeInOut", times: seconds.map((second) => second / 12) });
const STEPS = [
  { id: "upload", label: "Upload", icon: UploadCloud, description: "Choose your video" },
  { id: "create", label: "Create clips", icon: Scissors, description: "Turn moments into clips" },
  { id: "preview", label: "Preview", icon: Play, description: "Browse your favourites" },
  { id: "share", label: "Share", icon: Send, description: "TikTok, Instagram Reels & YouTube Shorts" },
];
const CHANNELS = [
  { id: "tiktok", label: "TikTok", icon: Music2, caption: "START WITH A STORY", duration: "0:24" },
  { id: "instagram", label: "Instagram Reels", icon: Instagram, caption: "KEEP THE GOOD PART", duration: "0:31" },
  { id: "youtube", label: "YouTube Shorts", icon: Youtube, caption: "MAKE IT COUNT", duration: "0:18" },
];

function UploadMoment() {
  return (
    <motion.div onUpdate={syncTimeline} data-testid="workflow-demo-upload" className="workflow-demo-upload"
      animate={{ opacity: [1, 1, 0, 0] }} transition={loop([0, 2.6, 3.1, 12])}>
      <motion.div onUpdate={syncTimeline} data-testid="workflow-simulated-upload-button" className="flex h-12 items-center justify-center gap-2 rounded-full bg-cyan-500 px-6 text-sm font-semibold text-black shadow-lg shadow-cyan-500/15"
        animate={{ scale: [1, 1, .94, 1, 1] }} transition={loop([0, 1.04, 1.12, 1.35, 12])}>
        <UploadCloud size={18} /> Upload video
      </motion.div>
      <motion.div onUpdate={syncTimeline} data-testid="workflow-demo-file-selected" className="mt-4 flex items-center justify-center gap-2 text-xs text-copy-400"
        animate={{ opacity: [0, 0, 1, 1, 0, 0] }} transition={loop([0, 1.4, 1.8, 2.7, 3.1, 12])}>
        <Check size={14} className="text-cyan-400" /> your-video.mp4
      </motion.div>
    </motion.div>
  );
}

function CreateMoment() {
  return (
    <>
      <motion.div onUpdate={syncTimeline} data-testid="workflow-demo-creating-label" className="absolute inset-x-0 top-6 flex items-center justify-center gap-2 text-xs font-medium text-copy-400"
        animate={{ opacity: [0, 0, 1, 1, 0, 0] }} transition={loop([0, 2.9, 3.3, 5.35, 5.8, 12])}>
        <Sparkles size={15} className="text-cyan-400" /> Finding the good parts
      </motion.div>
      <div className="workflow-demo-source-anchor">
        <motion.div onUpdate={syncTimeline} data-testid="workflow-demo-source" className="rounded-2xl border border-surface-700 bg-surface-950 p-4 text-center shadow-sm"
          animate={{ opacity: [0, 0, 1, 1, 0, 0], scale: [.9, .9, 1, 1, .7, .9] }} transition={loop([0, 2.6, 3.05, 3.5, 4.25, 12])}>
          <FileVideo className="mx-auto h-8 w-8 text-cyan-400" />
          <span className="mt-2 block text-[10px] font-semibold text-copy-300">your-video.mp4</span>
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-surface-800">
            <motion.div onUpdate={syncTimeline} className="h-full origin-left bg-cyan-500" animate={{ scaleX: [0, 0, 1, 1, 0] }} transition={loop([0, 3, 4, 11.8, 12])} />
          </div>
        </motion.div>
      </div>
    </>
  );
}

function DemoClip({ channel, index }) {
  const enter = 3.8 + index * .25, preview = 6 + index * .9, send = 9.2 + index * .22;
  const times = [0, enter, enter + .45, preview, preview + .2, preview + .7, send, send + .8, 11.5, 12];
  return (
    <div className="workflow-demo-clip-anchor" style={{ left: `${24 + index * 26}%` }}>
      <motion.div onUpdate={syncTimeline} data-testid={`workflow-demo-clip-${index}`} className="workflow-demo-clip"
        animate={{ opacity: [0, 0, 1, 1, 1, 1, 1, 0, 0, 0], scale: [.75, .75, 1, 1, 1.12, 1, 1, .2, .2, .75], y: ["15%", "15%", "0%", "0%", "-4%", "0%", "0%", "-85%", "-85%", "15%"] }} transition={loop(times)}>
        <span data-testid={`workflow-demo-duration-${index}`} className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-white">{channel.duration}</span>
        <div className={`workflow-demo-portrait workflow-demo-portrait-${index}`}><UserRound strokeWidth={1.2} className="h-12 w-12 sm:h-16 sm:w-16" /></div>
        <motion.div onUpdate={syncTimeline} data-testid={`workflow-demo-preview-${index}`} className="absolute inset-0 rounded-xl border-2 border-cyan-400 shadow-[0_0_18px_rgba(6,182,212,.2)]"
          animate={{ opacity: [0, 0, 1, 1, 0, 0] }} transition={loop([0, preview, preview + .12, preview + .6, preview + .78, 12])}>
          <div className="absolute inset-0 flex items-center justify-center"><Play size={22} className="fill-white text-white drop-shadow-md" /></div>
        </motion.div>
        <p data-testid={`workflow-demo-caption-${index}`} className="workflow-demo-caption">{channel.caption}</p>
        <div className="absolute inset-x-2 bottom-2 h-0.5 overflow-hidden rounded bg-white/20">
          <motion.div onUpdate={syncTimeline} data-testid={`workflow-demo-playback-${index}`} className="h-full origin-left bg-cyan-400" animate={{ scaleX: [0, 0, 1, 1, 0, 0] }} transition={loop([0, preview, preview + .68, 9, 9.3, 12])} />
        </div>
      </motion.div>
    </div>
  );
}

function SocialDestination({ channel, index }) {
  return (
    <div className="workflow-demo-channel-anchor" style={{ left: `${24 + index * 26}%` }}>
      <motion.div onUpdate={syncTimeline} data-testid={`workflow-demo-channel-${channel.id}`} className="flex flex-col items-center gap-2"
        animate={{ opacity: [0, 0, 1, 1, 0], y: [-5, -5, 0, 0, -5] }} transition={loop([0, 8.5, 8.95, 11.6, 12])}>
        <div className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-surface-700 bg-surface-950 text-copy-200">
          <channel.icon size={20} />
          <motion.span onUpdate={syncTimeline} data-testid={`workflow-demo-posted-${channel.id}`} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-500 text-black"
            animate={{ opacity: [0, 0, 1, 1, 0], scale: [.5, .5, 1, 1, .5] }} transition={loop([0, 9.85 + index * .22, 10.1 + index * .22, 11.6, 12])}><Check size={12} /></motion.span>
        </div>
        <span data-testid={`workflow-demo-channel-label-${channel.id}`} className="text-balance text-center text-[10px] font-medium text-copy-400 sm:text-xs">{channel.label}</span>
      </motion.div>
    </div>
  );
}

function ShareMoment() {
  return (
    <div className="workflow-demo-post-anchor">
      <motion.div onUpdate={syncTimeline} data-testid="workflow-simulated-post-button" className="flex h-9 items-center justify-center gap-2 rounded-full bg-cyan-500 px-5 text-xs font-semibold text-black"
        animate={{ opacity: [0, 0, 1, 1, 0, 0], scale: [1, 1, 1, .94, 1, 1] }} transition={loop([0, 8.6, 8.85, 9.12, 9.6, 12])}>
        <Send size={14} /> Post clips
      </motion.div>
    </div>
  );
}

function DemoCursor() {
  const clicks = [1.1, 6.2, 7.1, 8, 9.12];
  const pulseTimes = [0, ...clicks.flatMap((time) => [time, time + .08, time + .3]), 12];
  return (
    <motion.div onUpdate={syncTimeline} data-testid="workflow-demo-cursor" className="absolute z-20 h-6 w-6"
      animate={{
        left: ["32%", "50%", "50%", "54%", "20%", "24%", "24%", "50%", "50%", "76%", "76%", "50%", "50%", "57%", "57%"],
        top: [228, 158, 158, 216, 230, 196, 196, 196, 196, 196, 196, 304, 304, 322, 322],
        opacity: [0, 1, 1, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      }} transition={loop([0, .95, 1.35, 2.8, 5.7, 6.1, 6.6, 7, 7.5, 7.9, 8.35, 8.95, 9.25, 9.8, 12])}>
      <motion.span onUpdate={syncTimeline} data-testid="workflow-demo-click-ripple" className="absolute -left-2 -top-2 h-6 w-6 rounded-full border-2 border-cyan-400"
        animate={{ opacity: [0, ...clicks.flatMap(() => [0, .65, 0]), 0], scale: [0, ...clicks.flatMap(() => [.3, 1, 1.8]), 0] }} transition={loop(pulseTimes)} />
      <MousePointer2 className="relative h-6 w-6 fill-cyan-500 text-surface-950 drop-shadow-md" />
    </motion.div>
  );
}

function StaticWorkflow() {
  return (
    <div data-testid="workflow-static-fallback" className="grid grid-cols-2 gap-6 py-4 sm:grid-cols-4 sm:py-6">
      {STEPS.map((step) => <div key={step.id} data-testid={`workflow-static-${step.id}`} className="min-w-0 text-center">
        <step.icon aria-hidden="true" className="mx-auto mb-3 h-6 w-6 text-cyan-400" />
        <p className="text-sm font-semibold">{step.label}</p><p className="mt-1 text-xs text-copy-500">{step.description}</p>
      </div>)}
    </div>
  );
}

export function WorkflowGraphic() {
  const reducedMotion = useReducedMotion() !== false;
  return (
    <section data-testid="workflow-graphic-demo" aria-label="Video-to-clips demonstration" aria-describedby="workflow-description">
      <p id="workflow-description" className="sr-only" data-testid="workflow-description">A cursor uploads a video, three captioned clips are created and previewed, then shared to TikTok, Instagram Reels and YouTube Shorts in this animation. Social posting is a demonstration, not an available integration.</p>
      <div data-testid="workflow-animation" data-reduced-motion={reducedMotion} className="workflow-demo overflow-hidden">
        {reducedMotion ? <StaticWorkflow /> : <>
          <ol data-testid="workflow-stage-labels" className="mx-auto grid w-full max-w-5xl grid-cols-4 gap-2 px-0 pb-1 sm:gap-4 sm:px-4">
            {STEPS.map((step, index) => <li key={step.id} data-testid={`workflow-stage-${step.id}`} className="relative rounded-lg bg-surface-950 px-1 py-2 text-center text-[10px] font-medium text-copy-400 sm:text-xs">
              <span data-testid={`workflow-stage-label-${step.id}`}>{String(index + 1).padStart(2, "0")} · {step.label}</span>
              <motion.span onUpdate={syncTimeline} aria-hidden="true" className="absolute inset-x-2 bottom-0 h-0.5 origin-left rounded-full bg-cyan-500"
                animate={{ scaleX: [0, 0, 1, 1, 0] }} transition={loop([0, index * 3, (index + 1) * 3 - .2, 11.8, 12])} />
            </li>)}
          </ol>
          <div data-testid="workflow-animation-canvas" aria-hidden="true" className="workflow-scene">
            <UploadMoment /><CreateMoment />
            {CHANNELS.map((channel, index) => <DemoClip key={channel.id} channel={channel} index={index} />)}
            {CHANNELS.map((channel, index) => <SocialDestination key={channel.id} channel={channel} index={index} />)}
            <ShareMoment /><DemoCursor />
          </div>
        </>}
      </div>
    </section>
  );
}
